import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import DataFreightControlTable from "./components/data-freight-control-table";
import shipperService from "../../service/shipper.service";
import { getShipperDocCached } from "../../util/shipper/shipperDocCache";
import { canViewFreightControlPanel } from "../../util/freight/freight-control-permissions";
import {
  cardIconSx,
  clientListThemeVars,
} from "../../theme/globalStyle/globalStyle";
import "./freight-control-panel.css";

require("firebase/auth");

const ADVANCE_WHATSAPP_PHONE = "5581983422993";

function resolveShipperCompanyName(shipperData) {
  const data = shipperData || {};
  const personal = data.dataPersonal || {};
  return (
    String(personal.nameFantasy || "").trim() ||
    String(personal.socialName || "").trim() ||
    String(data.nameFantasy || "").trim() ||
    String(data.socialName || "").trim() ||
    ""
  );
}

function openAdvanceWhatsApp(companyName) {
  const name = String(companyName || "").trim() || "minha empresa";
  const message = `Olá gostaria de saber mais sobre a antecipação dos meus boletos: ${name}`;
  window.open(
    `https://wa.me/${ADVANCE_WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function FreightControlPanel() {
  const userLogado = useSelector((state) => state.usuarioLogado);
  const user = useSelector((state) => state.user);
  const [cleanFilters, setCleanFilters] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [companyName, setCompanyName] = useState("");

  const canView = canViewFreightControlPanel(user);

  useEffect(() => {
    if (userLogado > 0) return;
  }, [userLogado]);

  useEffect(() => {
    const shipperId = String(user?.uidShipper || "").trim();
    if (!shipperId) {
      setCompanyName("");
      return undefined;
    }

    let cancelled = false;

    getShipperDocCached(shipperId, async () => {
      const snap = await shipperService.getShipperAvailable(shipperId);
      return snap?.exists ? snap.data() : null;
    })
      .then((data) => {
        if (!cancelled) setCompanyName(resolveShipperCompanyName(data));
      })
      .catch(() => {
        if (!cancelled) setCompanyName("");
      });

    return () => {
      cancelled = true;
    };
  }, [user?.uidShipper]);

  if (userLogado <= 0) {
    return <Navigate to="/login" />;
  }

  if (!canView) {
    return <Navigate to="/" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <Box
          className="freight-control-panel"
          sx={pageWrapperSx}
          style={clientListThemeVars}
        >
          <Card sx={heroCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box className="freight-control-panel__hero">
                <Stack spacing={1} className="freight-control-panel__hero-copy">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={cardIconSx}>
                      <ReceiptLongOutlinedIcon sx={{ color: "#ffffff" }} />
                    </Box>
                    <Typography sx={heroTitleSx}>Faturas</Typography>
                  </Box>
                  <Typography sx={heroSubtitleSx}>
                    Consulte, filtre e acompanhe as faturas, boletos e status
                    financeiro dos fretes.
                  </Typography>
                </Stack>

                <Box className="freight-control-panel__actions">
                  <Button
                    variant="contained"
                    onClick={() => setCleanFilters(!cleanFilters)}
                    sx={primaryActionSx}
                  >
                    Limpar Filtros
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenFilters(!openFilters)}
                    sx={secondaryActionSx}
                  >
                    Filtros Salvos
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => openAdvanceWhatsApp(companyName)}
                    sx={highlightActionSx}
                  >
                    Antecipe seu boleto
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={contentCardSx}>
            <CardContent sx={{ p: { xs: 2, md: 2.4 } }}>
              <Box className="freight-control-panel__table-heading">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={cardIconSx}>
                    <TableRowsOutlinedIcon sx={{ color: "#ffffff" }} />
                  </Box>
                  <Box>
                    <Typography sx={sectionTitleSx}>Lista de faturas</Typography>
                    <Typography sx={sectionSubtitleSx}>
                      Use os filtros das colunas para localizar registros
                      específicos.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="freight-control-panel__table-shell">
                <DataFreightControlTable
                  cleanFilter={cleanFilters}
                  openFilter={openFilters}
                  onCleanFilterHandled={() => setCleanFilters(false)}
                  onOpenFilterHandled={() => setOpenFilters(false)}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      }
    />
  );
}

export default FreightControlPanel;

export {
  sendFreightInvoiceBoletoFromPanel,
  sendFreightInvoiceChargeFromPanel,
} from "./freightInvoiceEmailHandlers";

const pageWrapperSx = {
  p: { xs: 2, md: 3 },
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const heroCardSx = {
  borderRadius: "18px",
  border: "1px solid var(--client-primary-border)",
  boxShadow: "var(--client-shadow-soft)",
};

const contentCardSx = {
  borderRadius: "var(--client-radius-lg)",
  border: "1px solid var(--client-primary-border)",
  boxShadow: "var(--client-shadow-table)",
  background: "var(--client-surface-soft)",
};

const heroTitleSx = {
  color: "var(--client-primary)",
  fontWeight: 700,
  fontSize: "1.25rem",
};

const heroSubtitleSx = {
  color: "rgba(1, 36, 67, 0.74)",
  fontSize: "0.92rem",
  lineHeight: 1.5,
  maxWidth: 760,
};

const sectionTitleSx = {
  color: "var(--client-primary)",
  fontWeight: 700,
  fontSize: "1.05rem",
};

const sectionSubtitleSx = {
  color: "rgba(1, 36, 67, 0.62)",
  fontSize: "0.79rem",
  lineHeight: 1.45,
};

const primaryActionSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  backgroundColor: "var(--client-primary)",
  minHeight: 40,
  px: 2,
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "var(--client-primary-hover)",
  },
};

const secondaryActionSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  minHeight: 40,
  px: 2,
  whiteSpace: "nowrap",
  color: "var(--client-primary)",
  borderColor: "var(--client-primary-border)",
  "&:hover": {
    borderColor: "var(--client-primary)",
    backgroundColor: "var(--client-primary-soft)",
  },
};

const highlightActionSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  backgroundColor: "var(--client-highlight)",
  minHeight: 40,
  px: 2,
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#e02c00",
  },
};
